// Who can fill a slot (B-320, DIRECTION U21): what a lineup row opens beside
// the lineup, or in a sheet on a phone. Everyone the slot takes — a FLEX's
// three positions whoever is in it, the bench anyone, IR a status that
// qualifies — ranked by the season, each owned player with his team, mine
// marked, and Add or Claim on the rest. The player in the slot is marked
// where he falls, and the list scrolls to him.
//
// MY OWN PLAYER MOVES IN FROM HERE (B-379): a player of mine who can take the
// slot has **Move here** where a free agent has Add, which trades him into it
// as the lineup's own badges do. The first in the list was often my own bench
// player, and the way to him was back to the lineup's badges.
//
// NUMBERS, NEVER ADVICE (DIRECTION U1): the list ranks and marks; nothing on
// it calls anyone an upgrade. Who can fill the slot is the server's rule
// (`PlayersQuery.slot`), and so is the rank (B-321): this only draws them.
//
// Its switch and its order are kept per league on this device (UX-39); the
// slot itself is not, so My Team opens on the free agents (the plan, §2).

import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { failureOf, valueOf, PlayersQuery, type PlayerRowView } from '@fsp/domain';
import { useLocation } from 'react-router-dom';
import { usePlayers, useReaderZone } from '../../data/hooks';
import { useAtLeast } from '../../data/detail';
import { leagueIdFromPath } from '../../data/route';
import {
  EmptyState,
  GameLabel,
  positionLabel,
  PositionPill,
  ProTeamCode,
  StatusBadge,
} from '../../kit';
import { Button, Caption, Chips, Choice, IconButton } from '../../kit/ui';
import { useRemembered } from '../../shell/tabMemory';
import { num } from '../shared/notComputed';
import { Proj, ProjectionWhy } from '../shared/Projection';
import { fmtWhen, type RosterMove } from '../shared/RosterMoveSheet';
import type { PlayerCardData } from '../shared/PlayerCard';
import { rankText, seasonRankWords } from '../shared/rank';
import '../../styles/screens/players.css';
import { Term } from '../../kit/Why';
import { useTools } from '../../data/tools';

/** A lineup row's slot, chosen (B-320): which one, and who is in it now. */
export interface ChosenSlot {
  slot: string;
  slotIndex: number;
  /** The player in it, marked in the list; null for an empty slot. */
  playerId: string | null;
}

type Who = 'all' | 'free-agents';
type SlotSort = 'points' | 'average' | 'recent' | 'projection';

const ORDERS: readonly { value: SlotSort; label: string }[] = [
  { value: 'points', label: 'Season points' },
  { value: 'average', label: 'Points a game' },
  { value: 'recent', label: 'Last three games' },
  { value: 'projection', label: 'This week’s projection' },
];

function actionLabel(r: PlayerRowView): string {
  if (r.availability.kind === 'waivers' && r.availability.myClaimId) return 'Claimed';
  return r.action === 'claim' ? 'Claim' : 'Add';
}

/** "RB, WR or TE". */
function either(positions: readonly string[]): string {
  const said = positions.map(positionLabel);
  return said.length <= 1
    ? (said[0] ?? '')
    : `${said.slice(0, -1).join(', ')} or ${said[said.length - 1]}`;
}

export function SlotPanel({
  chosen,
  onOpen,
  onMove,
  onPlace,
  onClose,
}: {
  chosen: ChosenSlot;
  onOpen: (card: PlayerCardData) => void;
  onMove: (move: RosterMove) => void;
  /**
   * Move a player of mine into the chosen slot, trading places with whoever
   * is there; null when he cannot go there (locked, or a slot he cannot
   * play). Absent, no player of mine has the button.
   */
  onPlace?: (playerId: string) => (() => void) | null;
  /**
   * Back to the free agents: the slot unchosen. Absent in a phone's sheet,
   * whose own title and × say the same.
   */
  onClose?: () => void;
}) {
  const leagueKey = leagueIdFromPath(useLocation().pathname) ?? 'none';
  const [who, setWho] = useRemembered<Who>(`side:${leagueKey}:slotWho`, 'all');
  const [sortKept, setSort] = useRemembered<SlotSort>(`side:${leagueKey}:slotSort`, 'points');
  // No order or figure by a projection the league keeps none of (RULES_SCHEMA
  // §15c): one kept from before it was turned off is the season's points.
  const tools = useTools();
  const sort: SlotSort = sortKept === 'projection' && !tools.projections ? 'points' : sortKept;
  const standard = useAtLeast('standard');
  const pro = useAtLeast('pro');
  const zone = useReaderZone();
  const query = useMemo(
    () => PlayersQuery.parse({ availability: who, slot: chosen.slot, sort, limit: 100 }),
    [who, chosen.slot, sort],
  );
  const pool = usePlayers(query);
  const page = valueOf(pool.snapshot);
  const failure = failureOf(pool.snapshot);

  // TO THE PLAYER IN THE SLOT, once a list has him: where he stands is the
  // question the row was pressed to ask. Once per slot and list, so reading
  // on down is never pulled back.
  const list = useRef<HTMLDivElement>(null);
  const scrolled = useRef<string | null>(null);
  const listKey = `${chosen.slot}-${chosen.slotIndex}-${chosen.playerId}-${who}-${sort}`;
  useEffect(() => {
    if (page === null || scrolled.current === listKey) return;
    const here = list.current?.querySelector('[data-occupant]');
    if (here instanceof HTMLElement) {
      here.scrollIntoView({ block: 'center' });
      scrolled.current = listKey;
    }
  }, [page, listKey]);

  const act = (r: PlayerRowView) => {
    if (r.action !== 'add' && r.action !== 'claim') return;
    onMove({
      kind: r.action,
      playerId: r.player.id,
      claimId: r.availability.kind === 'waivers' ? r.availability.myClaimId : null,
    });
  };
  const open = (r: PlayerRowView) =>
    onOpen({
      player: r.player,
      game: r.game,
      projection: r.projection,
      basis: r.basis,
      points: null,
      statLine: null,
      action:
        r.action === 'add' || r.action === 'claim'
          ? { label: actionLabel(r), onClick: () => act(r) }
          : null,
    });

  const slot = page?.slot ?? null;
  const takes =
    slot === null
      ? null
      : slot.kind === 'reserve'
        ? `Players whose status lets them go on ${slot.name}`
        : slot.positions.length === 0
          ? 'Any position'
          : slot.positions.length === 1 && slot.positions[0] === slot.id
            ? null
            : either(slot.positions);

  /** Who holds him, the line's first words: the point of *All players*. */
  const holder = (r: PlayerRowView): string => {
    const a = r.availability;
    if (a.kind === 'mine') return r.player.id === chosen.playerId ? 'Yours, in this slot' : 'Yours';
    if (a.kind === 'owned') return a.team.name;
    if (a.kind === 'waivers')
      return zone !== null ? `On waivers until ${fmtWhen(a.clearsAt, zone)}` : 'On waivers';
    return 'Free agent';
  };

  /** The row's figure for the order chosen, labeled as every number here is (B-295). */
  const figure = (r: PlayerRowView, by: SlotSort) => {
    if (by === 'projection')
      return (
        <Proj>
          <ProjectionWhy player={r.player} projection={r.projection} basis={r.basis}>
            {num(r.projection)}
          </ProjectionWhy>
        </Proj>
      );
    const [value, tag] =
      by === 'points'
        ? [num(r.seasonPoints), 'pts']
        : by === 'average'
          ? [num(r.seasonAvg), 'a game']
          : [num(r.lastThreeAvg), 'last 3'];
    return (
      <span className="fsp-proj">
        {value}
        <span className="fsp-proj-tag">{tag}</span>
      </span>
    );
  };
  // At Everything, a second figure: the season beside a projection, the
  // projection beside the rest.
  // In a league that keeps no projections, the season's points beside its
  // points a game, and points a game beside the rest (§15c).
  const second: SlotSort =
    sort === 'projection'
      ? 'points'
      : tools.projections
        ? 'projection'
        : sort === 'average'
          ? 'points'
          : 'average';

  /** One row: the list's, and the pinned one for the player in the slot. */
  const row = (r: PlayerRowView) => {
    const mine = r.availability.kind === 'mine';
    const occupant = r.player.id === chosen.playerId;
    const position = r.player.positions[0]!;
    return (
      <div
        key={r.player.id}
        role="listitem"
        className="fsp-row fsp-row-clickable fsp-side-player fsp-slot-player"
        data-mine={mine || undefined}
        data-occupant={occupant || undefined}
        // THE NAME IS THE BUTTON; THE ROW IS A BIGGER TARGET FOR IT (B-275).
        data-ux-convenience=""
        onClick={() => open(r)}
      >
        {/* His position, and his rank there under it (B-321). */}
        <div className="fsp-slot-pos">
          <PositionPill pos={position} size="sm" />
          {standard && r.seasonRank !== null ? (
            <div className="fsp-sub fsp-players-rank">
              <Term tip={seasonRankWords(position, r.seasonRank)}>
                {rankText(position, r.seasonRank)}
              </Term>
            </div>
          ) : null}
        </div>
        <div className="fsp-side-who">
          <div className="fsp-name">
            <button
              type="button"
              className="fsp-linklike fsp-name-text fsp-name-btn"
              onClick={(e) => {
                e.stopPropagation();
                open(r);
              }}
            >
              {r.player.name}
            </button>
            <ProTeamCode code={r.player.proTeam} />
            <StatusBadge player={r.player} />
          </div>
          <div className="fsp-sub fsp-side-sub">
            <span className="fsp-slot-holder">{holder(r)}</span>
            {' · '}
            <GameLabel game={r.game} />
          </div>
        </div>
        <div className="fsp-right fsp-num fsp-side-num fsp-slot-num">
          {figure(r, sort)}
          {pro ? <div className="fsp-sub">{figure(r, second)}</div> : null}
        </div>
        {/* Keeps the row's own tap off the action's (data-ux-stop: not a control). */}
        <div data-ux-stop="" onClick={(e) => e.stopPropagation()}>
          {mine && !occupant && onPlace?.(r.player.id) ? (
            <Button
              size="xs"
              full
              variant="soft"
              label={`Move ${r.player.name} here`}
              onClick={() => onPlace(r.player.id)?.()}
            >
              Move here
            </Button>
          ) : r.action === 'add' || r.action === 'claim' ? (
            <Button
              size="xs"
              full
              variant={
                r.availability.kind === 'waivers' && r.availability.myClaimId
                  ? 'secondary'
                  : r.action === 'claim'
                    ? 'caution-soft'
                    : 'soft'
              }
              onClick={() => act(r)}
            >
              {actionLabel(r)}
            </Button>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="fsp-side-list fsp-slot-list">
      {/* Held at the top while the list scrolls to the player in the slot:
          what the list is, and the way back, stay in sight. */}
      <div className="fsp-slot-top">
        <div className="fsp-slot-head">
          <PositionPill pos={chosen.slot} />
          <div className="fsp-slot-title">
            {onClose !== undefined ? (
              <div className="fsp-slot-name">Who can fill this slot</div>
            ) : null}
            {takes !== null ? <div className="fsp-sub fsp-ui-wrap-text">{takes}</div> : null}
          </div>
          {onClose !== undefined ? (
            <IconButton
              icon="close"
              label="Back to free agents"
              variant="quiet"
              size="sm"
              onClick={onClose}
            />
          ) : null}
        </div>
        <div className="fsp-side-filters fsp-slot-filters">
          <Chips
            label="Whose players"
            single
            options={[
              { value: 'all', label: 'All players' },
              { value: 'free-agents', label: 'Available' },
            ]}
            value={[who]}
            onChange={(v) => setWho((v[0] as Who | undefined) ?? 'all')}
          />
          <Choice
            label="Order"
            hideLabel
            searchable={false}
            required
            value={sort}
            onChange={(v) => v && setSort(v as SlotSort)}
            options={ORDERS.filter((o) => tools.projections || o.value !== 'projection').map(
              (o) => ({ value: o.value, label: o.label }),
            )}
          />
        </div>
      </div>
      {/* The player in the slot, pinned when the list has not reached him. */}
      {page !== null &&
      chosen.playerId !== null &&
      !page.items.some((r) => r.player.id === chosen.playerId) ? (
        <InThisSlot
          slot={chosen.slot}
          playerId={chosen.playerId}
          sort={sort}
          further={who === 'all'}
          render={row}
        />
      ) : null}
      {page === null ? (
        <EmptyState>{failure?.message ?? 'Loading…'}</EmptyState>
      ) : page.items.length === 0 ? (
        <EmptyState>
          {who === 'free-agents'
            ? 'Nobody available can fill this slot.'
            : 'Nobody can fill this slot.'}
        </EmptyState>
      ) : (
        <div role="list" aria-label="Who can fill this slot" ref={list}>
          {page.items.map(row)}
        </div>
      )}
      {page !== null && (pool.more !== null || pool.loadingMore || pool.moreFailure !== null) ? (
        <div className="fsp-side-more">
          {pool.moreFailure !== null ? (
            <span className="fsp-sub fsp-ui-wrap-text">{pool.moreFailure}</span>
          ) : null}
          <Button
            size="xs"
            variant="quiet"
            loading={pool.loadingMore}
            disabled={pool.more === null && !pool.loadingMore}
            onClick={() => pool.more?.()}
          >
            Show more
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * THE PLAYER IN THE SLOT, PINNED, when the list has not reached him (the
 * review of 2026-10-14): a bench slot ranks every position together, so a
 * bench player can sit past the first page — and in the practice league,
 * which has no next page, out of reach — and *Available* never has him. Read
 * from my own players who can fill the slot, a short list.
 */
function InThisSlot({
  slot,
  playerId,
  sort,
  further,
  render,
}: {
  slot: string;
  playerId: string;
  sort: SlotSort;
  /** The list is everyone, and he is further down it. */
  further: boolean;
  render: (r: PlayerRowView) => ReactNode;
}) {
  const query = useMemo(
    () => PlayersQuery.parse({ availability: 'mine', slot, sort, limit: 50 }),
    [slot, sort],
  );
  const mine = valueOf(usePlayers(query).snapshot);
  const him = mine?.items.find((r) => r.player.id === playerId) ?? null;
  if (him === null) return null;
  return (
    <div className="fsp-slot-pin">
      <div className="fsp-side-section">
        <Caption>{further ? 'In this slot, further down the list' : 'In this slot'}</Caption>
      </div>
      <div role="list" aria-label="In this slot">
        {render(him)}
      </div>
    </div>
  );
}
